import { ReactNode } from 'react';
import type { MapStateToPropsFactory } from 'react-redux';
import { createSelector } from 'reselect';
import type { LimitOrderSelectors } from 'state/secondaryMarket/limit-order/limitOrder.selector';
import type { LimitOrderThunks } from 'state/secondaryMarket/limit-order/limitOrder.thunk';
import type { LiveSelectors } from 'state/secondaryMarket/live/live.selector';
import type { LiveThunks } from 'state/secondaryMarket/live/live.thunk';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { ReferentialSelector } from 'state/secondaryMarket/shared/referential/referential.selector';
import { toBcsProductKey } from 'state/secondaryMarket/utils';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';
import type { SecondaryWorkspaceType } from 'state/workspaces/model';
import type { BcsProductKey } from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.model';
import {
  getProductKey,
  getSecondaryMarketQuoteErrorKind,
  getSecondaryMarketQuoteErrorMessage,
  getSecondaryMarketQuoteLoadingStatus,
} from 'state/workspaces/secondary-market/quotes/secondaryMarketQuotes.selector';
import { TabLoadingStatus } from 'state/workspaces/tabs/tabs.model';
import { getTabIsFrozen, getTabLoadingStatus } from 'state/workspaces/tabs/tabs.selector';
import type { HttpErrorKind } from 'utils/errors/errors';

type OwnProps = Readonly<{
  tabId: string;
  sidePanel: ReactNode;
  children: ReactNode;
  workspaceType: SecondaryWorkspaceType;
}>;

type StateProps = Readonly<{
  bcsProductKey: BcsProductKey | undefined;
  isLoading: boolean;
  productName: string | undefined;
  traderLabel: string | undefined;
  hasFatalError: boolean;
  canResetForm: boolean;
  errors: SecondaryMarketFormError[];
  modifiedFields: readonly string[];
  errorKind?: HttpErrorKind;
  errorMessage?: string;
}>;

type DispatchProps = Readonly<{
  onResetForm: () => void;
  onRetryProductFetch?: () => void;
}>;

export type SecondaryMarketFormProps = OwnProps & StateProps & DispatchProps;

const noModifiedFields: readonly [] = [];

export const formConnectFactory = (
  referentialSelectors: ReferentialSelector,
  secondaryMarketSelector: LimitOrderSelectors | LiveSelectors,
  secondaryMarketThunks: LimitOrderThunks | LiveThunks,
) => {
  const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
    const getFormErrors = secondaryMarketSelector.makeGetValidationErrors();
    const getBcsProductKey = createSelector([getProductKey], toBcsProductKey);
    return (state, { tabId }): StateProps => {
      const bcsProductKey = getBcsProductKey(state, tabId);
      const dataLoadingStatus = getSecondaryMarketQuoteLoadingStatus(state, tabId);
      const tabContentLoadingStatus = getTabLoadingStatus(state, tabId);
      const productName = referentialSelectors.getProductName(state, tabId);
      const traderLabel = referentialSelectors.getTraderLabel(state, tabId);
      const hasFatalError =
        tabContentLoadingStatus === TabLoadingStatus.LoadingFailed || dataLoadingStatus === 'failed';
      const isLoading = tabContentLoadingStatus === TabLoadingStatus.Loading || dataLoadingStatus === 'loading';
      const isFrozen = getTabIsFrozen(state, tabId) ?? false;
      const errors = getFormErrors(state, tabId);
      const canResetForm = !isFrozen;
      const errorKind = getSecondaryMarketQuoteErrorKind(state, tabId);
      const errorMessage = getSecondaryMarketQuoteErrorMessage(state, tabId);
      return {
        bcsProductKey,
        productName,
        traderLabel,
        isLoading,
        hasFatalError,
        canResetForm,
        errors,
        modifiedFields: noModifiedFields,
        errorKind,
        errorMessage,
      };
    };
  };

  const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
    return {
      onResetForm: () => {
        dispatch(secondaryMarketThunks.resetTabThunk(tabId));
      },
      onRetryProductFetch: () => {
        dispatch(secondaryMarketThunks.reloadTabThunk(tabId));
      },
    };
  };
  return { mapDispatchToProps, mapStateToProps };
};

export type ResetFormProps = OwnProps & StateProps & DispatchProps;
